import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const EventTable = ({ events, onSort, sortConfig, onBulkAction, selectedEvents, onEventSelect, onEventAction }) => {
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    
    if (newSelectAll) {
      const allEventIds = events?.map(event => event?.id);
      onEventSelect(allEventIds);
    } else {
      onEventSelect([]);
    }
  };

  const handleEventSelect = (eventId) => {
    const isSelected = selectedEvents?.includes(eventId);
    let newSelected;
    
    if (isSelected) {
      newSelected = selectedEvents?.filter(id => id !== eventId);
    } else {
      newSelected = [...selectedEvents, eventId];
    }
    
    onEventSelect(newSelected);
    setSelectAll(newSelected?.length === events?.length);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: 'FileText' },
      published: { color: 'bg-blue-100 text-blue-800', icon: 'Eye' },
      live: { color: 'bg-green-100 text-green-800', icon: 'Radio' },
      completed: { color: 'bg-purple-100 text-purple-800', icon: 'CheckCircle' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: 'XCircle' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-foreground" />
      : <Icon name="ArrowDown" size={14} className="text-foreground" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedEvents?.length > 0 && (
        <div className="bg-muted border-b border-border px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {selectedEvents?.length} event{selectedEvents?.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => onBulkAction('publish')}>
                <Icon name="Eye" size={14} className="mr-1" />
                Publish
              </Button>
              <Button variant="outline" size="sm" onClick={() => onBulkAction('archive')}>
                <Icon name="Archive" size={14} className="mr-1" />
                Archive
              </Button>
              <Button variant="outline" size="sm" onClick={() => onBulkAction('duplicate')}>
                <Icon name="Copy" size={14} className="mr-1" />
                Duplicate
              </Button>
              <Button variant="outline" size="sm" onClick={() => onBulkAction('delete')} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Icon name="Trash2" size={14} className="mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Event</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => onSort('date')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Date</span>
                  {getSortIcon('date')}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => onSort('ticketsSold')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Tickets</span>
                  {getSortIcon('ticketsSold')}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => onSort('revenue')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Revenue</span>
                  {getSortIcon('revenue')}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => onSort('attendees')}
                  className="flex items-center space-x-1 hover:text-foreground"
                >
                  <span>Attendees</span>
                  {getSortIcon('attendees')}
                </button>
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {events?.map((event) => (
              <tr key={event?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedEvents?.includes(event?.id)}
                    onChange={() => handleEventSelect(event?.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                      <Image
                        src={event?.image}
                        alt={event?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{event?.name}</div>
                      <div className="text-sm text-muted-foreground">{event?.location}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {formatDate(event?.date)}
                </td>
                <td className="px-6 py-4">
                  {getStatusBadge(event?.status)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {event?.ticketsSold}/{event?.totalTickets}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-foreground">
                  {formatCurrency(event?.revenue)}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {event?.attendees}
                </td>
                <td className="px-6 py-4 text-right">
                  <EventActionMenu event={event} onAction={onEventAction} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {events?.map((event) => (
          <div key={event?.id} className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedEvents?.includes(event?.id)}
                  onChange={() => handleEventSelect(event?.id)}
                  className="rounded border-border"
                />
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={event?.image}
                    alt={event?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <EventActionMenu event={event} onAction={onEventAction} />
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-foreground">{event?.name}</h3>
              <p className="text-sm text-muted-foreground">{event?.location}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{formatDate(event?.date)}</span>
                {getStatusBadge(event?.status)}
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">{event?.ticketsSold}</div>
                  <div className="text-xs text-muted-foreground">Tickets Sold</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">{formatCurrency(event?.revenue)}</div>
                  <div className="text-xs text-muted-foreground">Revenue</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-foreground">{event?.attendees}</div>
                  <div className="text-xs text-muted-foreground">Attendees</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EventActionMenu = ({ event, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actions = [
    { label: 'Edit Event', icon: 'Edit', action: 'edit' },
    { label: 'View Details', icon: 'Eye', action: 'view' },
    { label: 'Manage Attendees', icon: 'Users', action: 'attendees' },
    { label: 'Duplicate', icon: 'Copy', action: 'duplicate' },
    { label: 'Analytics', icon: 'BarChart3', action: 'analytics' },
    { label: 'Archive', icon: 'Archive', action: 'archive', danger: true },
    { label: 'Delete Event', icon: 'Trash2', action: 'delete', danger: true }
  ];

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon name="MoreVertical" size={16} />
      </Button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-8 z-20 w-48 bg-popover border border-border rounded-lg shadow-lg py-1">
            {actions?.map((action) => (
              <button
                key={action?.action}
                onClick={() => {
                  onAction(event?.id, action?.action);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center px-3 py-2 text-sm hover:bg-muted transition-colors ${
                  action?.danger ? 'text-destructive' : 'text-foreground'
                }`}
              >
                <Icon name={action?.icon} size={14} className="mr-2" />
                {action?.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventTable;